import { currentLang } from "../i18n/lang.ts";

/**
 * Formatting.
 *
 * Money is stored in minor units and the multiplier differs per currency
 * (AED 100, BHD 1000). Nothing in the UI may divide by 100 by hand — that
 * mistake already inflated Bahrain's revenue tenfold once in an ad-hoc query.
 *
 * ЯЗЫК БЕРЁТСЯ ИЗ МОДУЛЬНОЙ ПЕРЕМЕННОЙ, а не приходит параметром. Причина
 * практическая: `money()` и `num()` вызываются примерно триста раз, и параметр
 * означал бы триста мест, где однажды забудут его передать, — а забытый
 * параметр здесь выглядит не как ошибка компиляции, а как английская цифра
 * посреди русского текста. Язык у документа один и меняется целиком, поэтому
 * чтение из модуля безопасно.
 *
 * Само ЧИСЛО от языка не зависит — меняются только разделители и суффиксы.
 * Правило округления одно на оба языка: иначе одна и та же выручка печаталась
 * бы разными цифрами, и сверка отчётов между языками стала бы невозможной.
 */

const MINOR: Record<string, number> = { AED: 100, BHD: 1000, USD: 100 };

export function minorUnits(currency: string | null | undefined): number {
  return MINOR[(currency ?? "AED").toUpperCase()] ?? 100;
}

/** Локаль для `Intl`: у русского запятая в дробях и узкий пробел в разрядах. */
function locale(): string {
  return currentLang() === "ru" ? "ru-RU" : "en-US";
}

export function money(
  minor: string | number | null | undefined,
  currency = "AED",
  opts: { compact?: boolean; withCode?: boolean } = {},
): string {
  const value = Number(minor ?? 0) / minorUnits(currency);
  const formatted = opts.compact
    ? compact(value)
    : value.toLocaleString(locale(), { maximumFractionDigits: 0 });
  return opts.withCode === false ? formatted : `${formatted} ${currency}`;
}

/**
 * Сокращение крупных чисел.
 *
 * Порог и число знаков ОДИНАКОВЫ на оба языка — меняется только суффикс и
 * разделитель дробной части. 436К и «436 тыс.» это одно и то же число,
 * записанное на двух языках; если бы у русского был другой порог, сверка
 * отчётов между языками потребовала бы пересчёта в уме.
 */
const COMPACT_SUFFIX = {
  en: { thousand: "K", million: "M" },
  ru: { thousand: " тыс.", million: " млн" },
} as const;

export function compact(n: number): string {
  const s = COMPACT_SUFFIX[currentLang()];
  const abs = Math.abs(n);
  const fixed = (v: number, digits: number): string =>
    v.toLocaleString(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits });

  if (abs >= 1_000_000) return `${fixed(n / 1_000_000, abs >= 10_000_000 ? 1 : 2)}${s.million}`;
  if (abs >= 1_000) return `${fixed(n / 1_000, abs >= 100_000 ? 0 : 1)}${s.thousand}`;
  return n.toLocaleString(locale(), { maximumFractionDigits: 0 });
}

export function num(n: number | string | null | undefined): string {
  return Number(n ?? 0).toLocaleString(locale());
}

/**
 * Процент.
 *
 * В русском между числом и знаком процента стоит пробел — это не вкус, это
 * норма набора. Пробел неразрывный: «13,4» и «%» не должны разъезжаться по
 * строкам в узкой ячейке таблицы.
 */
export function pct(part: number, total: number, digits = 1): string {
  if (!total) return "—";
  const value = ((100 * part) / total).toLocaleString(locale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return currentLang() === "ru" ? `${value} %` : `${value}%`;
}

/** Готовый процент из ответа — печатается тем же правилом, что и вычисленный. */
export function pctValue(value: number | string | null | undefined, digits = 1): string {
  const n = Number(value ?? 0);
  const text = n.toLocaleString(locale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return currentLang() === "ru" ? `${text} %` : `${text}%`;
}

/**
 * Дробное число с локальным разделителем.
 *
 * `toFixed()` печатает точку на любом языке, и «12.5 ч» посреди русского текста
 * читается как чужая цифра — ровно та же беда, что с процентами. Округление
 * одинаково на оба языка: меняется разделитель, а не значение.
 */
export function dec(n: number | string | null | undefined, digits = 1): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(locale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * «сентябрь 2026» / «September 2026» из строки `YYYY-MM`.
 *
 * Собирается из чисел, а не разбором строки конструктором `Date`: «2026-09»
 * он читает как UTC, и западнее Гринвича месяц съезжает на предыдущий.
 */
export function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)).toLocaleDateString(
    currentLang() === "ru" ? "ru-RU" : "en-GB",
    { month: "long", year: "numeric", timeZone: "UTC" },
  );
}

/** Последний день месяца строкой `YYYY-MM-DD`. День 0 следующего — последний текущего. */
export function monthEnd(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).toISOString().slice(0, 10);
}

export function shortDate(iso: string): string {
  const d = new Date(iso);
  // en-GB, а не en-US: «01 Jul» вместо «Jul 01». Порядок «день месяц» принят и
  // в ОАЭ, и в Бахрейне, и совпадает с русским.
  return d.toLocaleDateString(currentLang() === "ru" ? "ru-RU" : "en-GB", {
    day: "2-digit",
    month: "short",
  });
}

/**
 * Дата с днём недели: «Sat, 12 Sep» / «сб, 12 сент.».
 *
 * День недели нужен дайджесту: он про ОДНИ сутки, и «прошлая суббота» —
 * единственное, что делает сравнение с той же датой неделей раньше понятным без
 * календаря. Порядок «день месяц» и `en-GB` те же, что у `shortDate()`.
 */
export function weekdayDate(iso: string): string {
  const d = new Date(iso);
  // День без ведущего нуля: так дайджест печатал дату до перевода, и английский
  // текст меняться не должен.
  return d.toLocaleDateString(currentLang() === "ru" ? "ru-RU" : "en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** «2 minutes ago» — свежесть данных должна читаться без арифметики в уме. */
export function ago(iso: string | null): string {
  const ru = currentLang() === "ru";
  if (!iso) return ru ? "никогда" : "never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return ru ? "только что" : "just now";
  if (m < 60) return ru ? `${m} мин назад` : `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return ru ? `${h} ч назад` : `${h} h ago`;
  const d = Math.floor(h / 24);
  return ru ? `${d} дн. назад` : `${d} d ago`;
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Первое число текущего месяца — начало периода по умолчанию.
 *
 * Берётся из `todayISO()`, а не из местного календаря: конец периода по
 * умолчанию — тот же `todayISO()`, и в первые часы месяца по Дубаю (в UTC ещё
 * прошлый месяц) два разных «сегодня» дали бы начало позже конца.
 */
export function monthStartISO(): string {
  return `${todayISO().slice(0, 7)}-01`;
}

/**
 * Дата на `n` дней раньше — по умолчанию сегодняшней.
 *
 * `from` нужен спискам утреннего дайджеста: его окно отсчитывается от
 * ОТЧЁТНОГО дня, а не от сегодняшнего. Дайджест смотрят через день и через
 * два, и отсчёт от «сегодня» отобрал бы не ту неделю, которую посчитала
 * карточка.
 *
 * Полдень, а не полночь: `toISOString` переводит в UTC, и дата, собранная на
 * местной полуночи западнее Гринвича, съехала бы на сутки назад.
 */
export function daysAgoISO(n: number, from?: string): string {
  const d = from === undefined ? new Date() : new Date(`${from}T12:00:00`);
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export const BRAND_COLOR: Record<string, string> = {
  leoloona: "#00C1DE",
  hellopark: "#FFB600",
  zamania: "#ED40A9",
};
